import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, ArrowLeft, DatabaseBackup, Download, Info, Archive, ExternalLink, History, ChevronDown, Clock, AlertTriangle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from '@/components/ui/use-toast';
import { useAutomation } from '@/hooks/useAutomation';
import { useJotformPlanMatch } from '@/hooks/useJotformPlanMatch';
import { useTimezone } from '@/contexts/TimezoneContext';
import { getBillingStatusLabel, hasActiveBillingAccess, hasBillingHistory } from '@/lib/billingAccess';
import { getJotformBackupFormName } from '@/lib/jotformBackupMetadata';
import { formatGraceDeadline, getGraceDaysRemaining } from '@/lib/jotformPlanGrace';
import {
  fetchJotformForms,
  fetchJotformBackups,
  backupJotformForm,
  restoreJotformForm,
  type JotformForm,
  type JotformBackup,
} from '@/services/jotformService';

const JotformFormDetailPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const { formatDate } = useTimezone();

  const { planUsage, organization, getTrackedForm } = useAutomation();
  const jotformPlanMatch = useJotformPlanMatch();
  const retentionDays = planUsage?.retentionDays ?? null;
  const billingHasHistory = hasBillingHistory(organization);
  const billingAccessActive = hasActiveBillingAccess(organization);
  const billingSuspended = billingHasHistory && !billingAccessActive;
  const upgradeRequired = Boolean(jotformPlanMatch.data?.upgradeRequired);
  const mismatchEnforced = upgradeRequired && Boolean(jotformPlanMatch.data?.enforcementActive);
  const graceEndsLabel = formatGraceDeadline(jotformPlanMatch.data?.graceEndsAt);
  const graceDaysRemaining = getGraceDaysRemaining(jotformPlanMatch.data?.graceEndsAt);

  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);
  const [selectedBackupForRestore, setSelectedBackupForRestore] = useState<JotformBackup | null>(null);
  const [typedConfirmName, setTypedConfirmName] = useState("");

  // Fetch forms list to get form name and metadata
  const { data: forms } = useQuery<JotformForm[], Error>({
    queryKey: ['jotformForms'],
    queryFn: fetchJotformForms,
    enabled: !!formId,
  });

  const currentForm = useMemo(() => {
    return forms?.find(f => f.id === formId) || null;
  }, [forms, formId]);

  const currentFormName = currentForm?.name || `Form ${formId}`;
  const formAccessBlockedByMismatch = useMemo(() => {
    if (!mismatchEnforced || !formId || !forms?.length) return false;

    const formLimit = planUsage?.formLimit ?? Infinity;
    if (formLimit === Infinity) return false;

    const statusActive = forms.filter(f => f.status !== 'DELETED' && f.status !== 'DISABLED' && f.archived !== true);
    const trackedIds = statusActive
      .filter(f => !!getTrackedForm(f.id, 'jotform'))
      .map(f => f.id);
    const untrackedIds = statusActive
      .filter(f => !getTrackedForm(f.id, 'jotform'))
      .map(f => f.id);
    const coveredIds = new Set([
      ...trackedIds.slice(0, formLimit),
      ...untrackedIds.slice(0, Math.max(0, formLimit - trackedIds.length)),
    ]);

    return !coveredIds.has(formId);
  }, [formId, forms, getTrackedForm, mismatchEnforced, planUsage]);
  const interactiveAccessBlocked = billingSuspended || formAccessBlockedByMismatch;

  const { data: backups, isLoading: isLoadingBackups, error: backupsError, refetch: refetchBackups } = useQuery<JotformBackup[], Error>({
    queryKey: ['jotformBackups', formId],
    queryFn: () => fetchJotformBackups(formId!),
    enabled: !!formId && !interactiveAccessBlocked,
  });

  const backupFormMutation = useMutation({
    mutationFn: () => backupJotformForm(formId!),
    onSuccess: (data) => {
      toast({
        title: "Backup Successful",
        description: `Form '${currentFormName}' backed up successfully (${data.backup.questionsCount} questions)`,
      });
      queryClient.invalidateQueries({ queryKey: ['jotformBackups', formId] });
      queryClient.invalidateQueries({ queryKey: ['allJotformBackups'] });
    },
    onError: (error: Error) => {
      if (error.message === "REAUTH_REQUIRED") {
        toast({
          variant: "destructive",
          title: "Backup Failed",
          description: "Re-authentication required. Please disconnect and connect Jotform again."
        });
        return;
      }
      toast({
        variant: "destructive",
        title: "Backup Failed",
        description: error.message || "Could not backup form.",
      });
    },
  });

  const restoreFormMutation = useMutation({
    mutationFn: ({ backupId, newFormName }: { backupId: string; newFormName: string }) =>
      restoreJotformForm(backupId, newFormName),
    onSuccess: (data) => {
      toast({
        title: "Restore Successful",
        description: `Form '${data.formName}' created successfully via ${data.method}.`,
      });
      setIsRestoreConfirmOpen(false);
      setSelectedBackupForRestore(null);
      setTypedConfirmName("");
      queryClient.invalidateQueries({ queryKey: ['jotformForms'] });
    },
    onError: (error: Error) => {
      if (error.message === "REAUTH_REQUIRED") {
        toast({
          variant: "destructive",
          title: "Restore Failed",
          description: "Re-authentication required. Please disconnect and connect Jotform again."
        });
        return;
      }
      toast({
        variant: "destructive",
        title: "Restore Failed",
        description: error.message || "Could not restore form from backup.",
      });
    },
  });

  const handleDownloadBackup = (backup: JotformBackup) => {
    const formName = getJotformBackupFormName(backup);
    const filename = `jotform-backup-${formName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${backup.id.slice(0, 8)}.json`;

    const blob = new Blob([JSON.stringify(backup.form_data_json, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleOpenRestoreDialog = (backup: JotformBackup) => {
    setSelectedBackupForRestore(backup);
    setTypedConfirmName('');
    setIsRestoreConfirmOpen(true);
  };

  const handleConfirmRestore = () => {
    if (!selectedBackupForRestore) return;
    restoreFormMutation.mutate({
      backupId: selectedBackupForRestore.id,
      newFormName: `FAB RESTORE - ${currentFormName}`,
    });
  };

  if (!formId) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-grow container mx-auto py-8">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>Invalid Form ID provided.</AlertDescription>
          </Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow container mx-auto py-8 px-4">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>

        {billingSuspended && (
          <Alert className="mb-6 border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Billing inactive</AlertTitle>
            <AlertDescription>
              {getBillingStatusLabel(organization?.billing_status)}. Automatic backups still continue, but backup history, download, restore, and manual backup actions are unavailable until billing is reinstated.
            </AlertDescription>
          </Alert>
        )}
        {!billingSuspended && upgradeRequired && !mismatchEnforced && (
          <Alert className="mb-6 border-blue-300 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Upgrade grace period active</AlertTitle>
            <AlertDescription>
              Your Jotform account now maps to a higher FAB plan.
              {graceEndsLabel
                ? ` Upgrade FAB by ${graceEndsLabel}${graceDaysRemaining !== null ? ` (${graceDaysRemaining} day${graceDaysRemaining === 1 ? '' : 's'} remaining)` : ''} to keep this form and all backup-history views fully accessible.`
                : ' Upgrade FAB within your 14-day grace period to keep this form and all backup-history views fully accessible.'}
              <div className="mt-3">
                <Button size="sm" onClick={() => navigate('/select-tier?action=upgrade')}>
                  Review Upgrade
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {!billingSuspended && formAccessBlockedByMismatch && (
          <Alert className="mb-6 border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Upgrade required for this form</AlertTitle>
            <AlertDescription>
              This form now sits above your current FAB visibility limit, and the grace period
              {graceEndsLabel ? ` ended on ${graceEndsLabel}` : ' has ended'}.
              Automatic backups still continue, but backup history, download, restore, and manual backup actions are unavailable until FAB is upgraded.
              <div className="mt-3">
                <Button size="sm" onClick={() => navigate('/select-tier?action=upgrade')}>
                  Upgrade FAB
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Form Info Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{currentFormName}</CardTitle>
            <CardDescription>
              ID: {formId}
              {currentForm && (
                <> - Status: {currentForm.status} - {currentForm.count} submissions</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {currentForm?.url && !formAccessBlockedByMismatch && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="text-primary border-primary hover:text-primary hover:bg-primary/10 cursor-pointer"
                >
                  <a href={currentForm.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" /> View on Jotform
                  </a>
                </Button>
              )}
              {currentForm?.url && formAccessBlockedByMismatch && (
                <Button variant="outline" size="sm" disabled>
                  <ExternalLink className="h-4 w-4 mr-1" /> View on Jotform
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => backupFormMutation.mutate()}
                disabled={interactiveAccessBlocked || backupFormMutation.isPending}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {backupFormMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Archive className="h-4 w-4 mr-1" />
                )}
                {interactiveAccessBlocked ? 'Unavailable' : 'Backup Form'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Backups Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DatabaseBackup className="mr-2 h-5 w-5" /> Form Backups
            </CardTitle>
            <CardDescription>List of all backups for this form. Restore creates a NEW form in Jotform.</CardDescription>
          </CardHeader>
          <CardContent>
            {interactiveAccessBlocked && (
              <Alert>
                <AlertTitle>Backup history hidden</AlertTitle>
                <AlertDescription>
                  {billingSuspended
                    ? 'FAB is still preserving and taking backups for this form, but history access is disabled while billing is inactive.'
                    : 'FAB is still preserving and taking backups for this form, but history access is disabled until the matched FAB upgrade is completed.'}
                </AlertDescription>
              </Alert>
            )}
            {!interactiveAccessBlocked && isLoadingBackups && (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="ml-2 text-muted-foreground">Loading backups...</p>
              </div>
            )}
            {!interactiveAccessBlocked && backupsError && (
              <Alert variant="destructive">
                <AlertTitle>Error Loading Backups</AlertTitle>
                <AlertDescription>
                  {backupsError.message}
                  <Button variant="link" onClick={() => refetchBackups()} className="ml-2">Try again</Button>
                </AlertDescription>
              </Alert>
            )}
            {!interactiveAccessBlocked && !isLoadingBackups && !backupsError && (!backups || backups.length === 0) && (
              <p className="text-muted-foreground">No backups found for this form.</p>
            )}
            {!interactiveAccessBlocked && backups && backups.length > 0 && (
              <TooltipProvider>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Form Name (at backup)</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            Created At
                            <ChevronDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                        <TableHead>Retention</TableHead>
                        <TableHead>Backup ID</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backups.map((backup) => {
                        const backupFormName = getJotformBackupFormName(backup, 'display');
                        return (
                          <TableRow key={backup.id}>
                            <TableCell className="text-xs">
                              {backupFormName.length > 50 ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help">{backupFormName.substring(0, 50)}...</span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{backupFormName}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                backupFormName
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={
                                backup.kind === 'auto'
                                  ? 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }>
                                {backup.kind === 'auto' ? 'Auto' : 'Manual'}
                              </span>
                            </TableCell>
                            <TableCell>{formatDate(backup.created_at)}</TableCell>
                            <TableCell>
                              {(() => {
                                if (!retentionDays) return <span className="text-xs text-muted-foreground">—</span>;
                                const expiresAt = new Date(new Date(backup.created_at).getTime() + retentionDays * 24 * 60 * 60 * 1000);
                                const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                return (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-help">
                                        <Clock className="h-3 w-3" />
                                        {daysLeft > 0 ? `${daysLeft}d left` : 'Expiring soon'}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Expires {formatDate(expiresAt, 'MMM d, yyyy')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{backup.id}</TableCell>
                            <TableCell className="space-x-2 flex items-center">
                              <Button variant="outline" size="sm" onClick={() => handleDownloadBackup(backup)}>
                                <Download className="mr-1 h-4 w-4" /> Download
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-500 bg-white text-red-500 hover:bg-red-50 hover:text-red-600 focus:ring-red-500"
                                onClick={() => handleOpenRestoreDialog(backup)}
                                disabled={restoreFormMutation.isPending && selectedBackupForRestore?.id === backup.id}
                              >
                                {restoreFormMutation.isPending && selectedBackupForRestore?.id === backup.id ? (
                                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                ) : (
                                  <History className="mr-1 h-4 w-4" />
                                )}
                                Restore
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </TooltipProvider>
            )}
          </CardContent>
        </Card>

        {/* Raw JSON Viewer */}
        {!interactiveAccessBlocked && backups && backups.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Info className="mr-2 h-5 w-5" /> Form Details
              </CardTitle>
              <CardDescription>Raw JSON data from the latest backup.</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion
                type="multiple"
                className="w-full"
                value={openAccordionItems}
                onValueChange={setOpenAccordionItems}
              >
                <AccordionItem value="form-json">
                  <AccordionTrigger>Form Data (JSON)</AccordionTrigger>
                  <AccordionContent>
                    <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-h-96">
                      {JSON.stringify(backups[0].form_data_json, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>
                {backups[0].canonical_data && Object.keys(backups[0].canonical_data).length > 0 && (
                  <AccordionItem value="canonical-data">
                    <AccordionTrigger>Canonical Data</AccordionTrigger>
                    <AccordionContent>
                      <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-h-96">
                        {JSON.stringify(backups[0].canonical_data, null, 2)}
                      </pre>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Restore Confirmation Dialog */}
      {selectedBackupForRestore && (
        <AlertDialog open={isRestoreConfirmOpen} onOpenChange={setIsRestoreConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Restore from Backup</AlertDialogTitle>
                <AlertDialogDescription>
                  This will create a <strong>NEW</strong> form on Jotform using the backup
                  created on{' '}
                  {formatDate(selectedBackupForRestore.created_at)}.
                  <br />
                  The new form will be named "FAB RESTORE - {currentFormName}".
                  <br />
                To confirm, please type the form name, "{currentFormName}", in the box below.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <label htmlFor="confirm-restore-input" className="sr-only">Type the form name to confirm</label>
              <Input
                id="confirm-restore-input"
                value={typedConfirmName}
                onChange={(e) => setTypedConfirmName(e.target.value)}
                placeholder="Type form name to confirm"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmRestore}
                disabled={typedConfirmName !== currentFormName || restoreFormMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {restoreFormMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default JotformFormDetailPage;
